import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { SshKeyFetch200 } from '@newfold/huapi-js/src/index.schemas';

import { ReactComponent as SshKeyIcon } from '~/assets/images/sshkey-icon.svg';
import Copy from '~/components/Copy';
import ResponsiveDialog, { StyledButton } from '~/components/ResponsiveDialog';
import ModalHeader from '~/Nova/scenes/HostingList/scenes/SelfManagedHosting/scenes/SelfManagedHostingLayout/AiTooling/components/ManageApiKeysModal/ModalHeader';
import { formattedCreatedAt as formatCreatedAt } from '~/utils/date/helpers';

import { getKeyType } from '../utils/helper';
import ViewKeyLoading from './ViewKeyLoading';

type ViewKeyProps = {
  keyDetails: {
    keyData: SshKeyFetch200;
    createdAt: string;
  };
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isSSHKeyLoading: boolean;
};

const copyIconWrapper = {
  direction: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
} as const;

const keyValueFieldSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: `${theme.palette.common.white} !important`,
    alignItems: 'flex-start',
  },
  '& .MuiOutlinedInput-input': {
    backgroundColor: `${theme.palette.common.white} !important`,
    fontSize: 14,
    lineHeight: 1.5,
    padding: '10px 12px',
  },
  '& textarea': {
    backgroundColor: `${theme.palette.common.white} !important`,
  },
});

const ViewKey = ({
  keyDetails: {
    keyData: { key_name: keyName = '', key: keyValue },
    createdAt,
  },
  open,
  setOpen,
  isSSHKeyLoading,
}: ViewKeyProps) => {
  const { t } = useTranslation('sshKeyManager');
  const keyType = keyValue ? getKeyType(keyValue) : '';
  const theme = useTheme();
  const formattedCreatedAt = formatCreatedAt(createdAt);

  const metadata = [
    keyType,
    formattedCreatedAt && `${t('added', 'Added')} ${formattedCreatedAt}`,
  ]
    .filter(Boolean)
    .join(' · ');

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ResponsiveDialog open={open} data-testid="ssh-view-key-dialog">
      <ModalHeader
        icon={<SshKeyIcon />}
        title={keyName}
        onClose={handleClose}
      />

      <DialogContent>
        <Divider sx={{ mb: 2 }} />
        {isSSHKeyLoading ? (
          <ViewKeyLoading />
        ) : (
          <Stack spacing={1.5}>
            {keyValue && (
              <>
                <Copy
                  icon={<ContentCopyOutlinedIcon fontSize="small" />}
                  copyValue={keyValue}
                  color="primary"
                  stackProps={copyIconWrapper}
                  showLabel
                >
                  <Typography variant="body2">{t('sshPublicKey')}</Typography>
                </Copy>
                <TextField
                  value={keyValue}
                  variant="outlined"
                  multiline
                  fullWidth
                  rows={6}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={keyValueFieldSx(theme)}
                />
              </>
            )}

            {metadata && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {metadata}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <StyledButton variant="contained" onClick={handleClose}>
          {t('close')}
        </StyledButton>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ViewKey;
